import { motion } from 'framer-motion'
import { useCallback, useEffect, useMemo, useState } from 'react'
import css from 'classnames'
import { observer } from 'mobx-react-lite'
import { useLocation } from 'react-router-dom'
import { menuLinks, useWindowSize } from '~/shared/lib'
import { SVGMenu } from '~/shared/ui'
import { SidebarHeader } from './components/header'
import { NavMenu } from './components/nav-menu'
import { mobileSidebarAnimation, sidebarAnimation } from './model'
import { ExpressOrderIndicator } from '~/features/express-order-indicator'
import { isDemoEnvironment } from '~/shared/lib/environment'

export const SideBar = observer(() => {
  const { width } = useWindowSize()
  const { pathname } = useLocation()

  const isTab = useMemo(() => !!(Number(width) < 768), [width])

  const [isOpen, setIsOpen] = useState(!isTab)
  const sidebarTopClass = isDemoEnvironment ? 'top-10' : 'top-0'
  const sidebarPanelTopClass = isDemoEnvironment ? 'top-10 md:top-0' : 'top-0'
  const sidebarHeightClass = isDemoEnvironment
    ? 'h-[calc(100vh-40px)]'
    : 'h-screen'
  const desktopSidebarHeightClass = isDemoEnvironment
    ? 'md:h-[calc(100vh-40px)]'
    : 'md:h-screen'
  const toggleMenu = useCallback(() => setIsOpen((prev) => !prev), [])
  const handleOpenMenu = useCallback(() => setIsOpen(true), [])

  useEffect(() => {
    if (isTab) {
      setIsOpen(false)
    } else {
      setIsOpen(true)
    }
  }, [isTab])

  useEffect(() => {
    if (isTab) {
      setIsOpen(false)
    }
  }, [pathname, isTab])

  return (
    <aside
      className={`sticky left-0 ${sidebarTopClass} ${desktopSidebarHeightClass} z-[999]`}
    >
      <div
        onClick={() => setIsOpen(false)}
        className={css(
          `md:hidden fixed inset-x-0 bottom-0 ${sidebarTopClass} ${sidebarHeightClass} z-[999] bg-black/50`,
          {
            block: isOpen,
            hidden: !isOpen,
          },
        )}
      />
      <motion.div
        variants={isTab ? mobileSidebarAnimation : sidebarAnimation}
        animate={isOpen ? 'open' : 'closed'}
        initial={{ x: isTab ? -250 : 0 }}
        className={`bg-white text-gray shadow-xl z-[999] w-[16rem] max-width-[16rem] ${sidebarHeightClass} ${sidebarPanelTopClass} pt-3 overflow-y-hidden md:relative fixed flex flex-col flex-1`}
      >
        <SidebarHeader toggleMenu={toggleMenu} isOpen={isOpen} />

        <NavMenu isOpen={isOpen} items={menuLinks} openMenu={handleOpenMenu} />
      </motion.div>

      <div
        className="relative p-3 md:hidden w-full bg-white flex shadow"
        onClick={() => setIsOpen(true)}
      >
        <SVGMenu />
        <ExpressOrderIndicator
          variant="dot"
          className="absolute left-8 top-1"
        />
      </div>
    </aside>
  )
})
